import { Element } from "@xmpp/xml"
import { PastRouteStop, Route2g, RouteStop } from "../reducers/routes2gSlice"
import LocationParser from "./locationParser"

class Route2gParser {
  static readonly NAMESPACE = "urn:slickshift:route:0"

  static parse(stanza: Element): Route2g | undefined {
    const route = this.getCurrent(stanza)?.getChild("route", this.NAMESPACE)

    if (!route) return

    const lastUpdatedBy = route.getAttr("last_updated_by")

    const stops = route
      .getChildren("stop")
      .map((item) => this.parseRouteStop(item))
      .filter((stop) => stop !== undefined) as RouteStop[]

    return {
      jid: stanza.attrs.from,
      lastUpdatedBy,
      stops: stops,
      pastStops: this.parsePastStops(route),
    }
  }

  /** The `<past-stop>` children of a `<route>` element; undefined when none. */
  static parsePastStops(routeElement: Element): PastRouteStop[] | undefined {
    const pastStops = routeElement
      .getChildren("past-stop")
      .map((item) => this.parsePastStop(item))
      .filter((stop) => stop !== undefined) as PastRouteStop[]

    return pastStops.length > 0 ? pastStops : undefined
  }

  static parsePastStop(item: Element): PastRouteStop | undefined {
    const geoloc = item.getChild("geoloc")
    if (!geoloc) return undefined
    const location = LocationParser.parseGeoloc(geoloc)
    if (!location) return undefined

    const status = item.getAttr("proximity_status")

    return {
      id: item.getAttr("id"),
      location,
      action: item.getAttr("action") || undefined,
      ref: item.getAttr("ref") || undefined,
      status: status === "left" || status === "skipped" ? status : undefined,
      arrived_at: item.getAttr("arrived_at") || undefined,
      order_timestamp: item.getAttr("delivery_order_timestamp") || undefined,
    }
  }

  static parseRouteStops(stanza: Element): Route2g["stops"] | undefined {
    return stanza
      .getChildren("stop")
      .map((item) => this.parseRouteStop(item))
      .filter((stop) => stop !== undefined) as RouteStop[]
  }

  static parseRouteStop(item: Element): RouteStop | undefined {
    const geoloc = item.getChild("geoloc")
    if (!geoloc) {
      return undefined
    }
    const location = LocationParser.parseGeoloc(geoloc)
    if (!location) {
      return undefined
    }
    const sourceMetadataRaw = item.getAttr("source_metadata")
    let sourceMetadata: Record<string, unknown> | undefined
    if (sourceMetadataRaw) {
      try {
        sourceMetadata = JSON.parse(sourceMetadataRaw) as Record<
          string,
          unknown
        >
      } catch (error) {
        console.error("Failed to parse route stop source_metadata JSON:", error)
      }
    }

    return {
      id: item.getAttr("id"),
      by: item.getAttr("by"),
      location: location,
      msgId: item.getAttr("msg_id"),
      action: item.getAttr("action"),
      action_reference_number: item.getAttr("action_reference_number"),
      avoid: (() => {
        const avoidAttr = item.getAttr("avoid")
        if (avoidAttr === undefined) return undefined
        if (avoidAttr === "") return []
        return avoidAttr.split(",").filter((s: string) => s.trim() !== "")
      })(),
      ref: item.getAttr("ref"),
      remaining_distance: this.parseIntIfNotNull(
        item.getAttr("remaining_distance"),
      ),
      remaining_time: this.parseIntIfNotNull(item.getAttr("remaining_time")),
      scheduled_arrival_time: item.getAttr("scheduled_arrival_time"),
      estimated_arrival_time: item.getAttr("estimated_arrival_time"),
      estimated_arrival_time_with_tacho: item.getAttr(
        "estimated_arrival_time_with_tacho",
      ),
      assumed_rest_until: item.getAttr("assumed_rest_until") || undefined,
      arrived_at: item.getAttr("arrived_at") || undefined,
      proximity_status: item.getAttr("proximity_status"),
      order_timestamp: item.getAttr("delivery_order_timestamp") || undefined,
      timeliness_status: item.getAttr("timeliness_status"),
      source_metadata: sourceMetadata,
      external_status: item.getAttr("external_status"),
      external_status_at: item.getAttr("external_status_at"),
      external_status_source: item.getAttr("external_status_source"),
      shouldAlert: this.parseBooleanIfNotNull(item.getAttr("should_alert")),
      isApproximate: this.parseBooleanIfNotNull(item.getAttr("is_approximate")),
      alertMetadata: item.getAttr("alert_metadata"),
    }
  }

  static parseIntIfNotNull(value: string | null): number | undefined {
    return value !== null ? parseInt(value, 10) : undefined
  }

  static parseBooleanIfNotNull(value: string | null): boolean | undefined {
    if (!value) return undefined

    const lowercaseValue = value.toLowerCase()

    if (
      lowercaseValue === "true" ||
      lowercaseValue === "1" ||
      lowercaseValue === "yes"
    ) {
      return true
    }
    if (
      lowercaseValue === "false" ||
      lowercaseValue === "0" ||
      lowercaseValue === "no"
    ) {
      return false
    }

    return undefined
  }

  static getCurrent(stanza: Element) {
    const pubSubEventNode =
      stanza.getChild("event", "http://jabber.org/protocol/pubsub#event") ||
      stanza.getChild("pubsub", "http://jabber.org/protocol/pubsub")

    return pubSubEventNode
      ?.getChildByAttr("node", this.NAMESPACE)
      ?.getChildByAttr("id", "current")
  }
}

export default Route2gParser
